import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Spin } from 'antd'
import { addPlayerToRoster, deleteRoster, editRoster, getRosterForGameInLeague } from 'services/roster'
import { getPlayersToTeam } from 'services/players-to-teams'
import { PlayerToTeam } from 'types/players-to-teams'
import { Roster, RosterFormData } from 'types/roster'
import { RosterTable } from '../roster-table'
import { RosterTransfer } from '../roster-transfer'
import { RosterForm } from '../roster-form'
import { modalTitle as formatModalTitle } from 'utils/modal-title'

export type SelectedRosterProps = {
    teamId: number
    leagueId: number
    gameId: number
}

export const RosterCreator = memo((props: SelectedRosterProps) => {
    const { teamId, leagueId, gameId } = props

    const [roster, setRoster] = useState<Roster[]>([])
    const [players, setPlayers] = useState<PlayerToTeam[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [open, setOpen] = useState<boolean>(false)
    const [selectedRoster, setSelectedRoster] = useState<Roster>()

    const fetchRosters = useCallback(() => {
        return getRosterForGameInLeague(teamId, leagueId, gameId).then(rosters => {
            setRoster(rosters)
        })
    }, [gameId, leagueId, teamId])

    useEffect(() => {
        setLoading(true)
        Promise.all([fetchRosters(), getPlayersToTeam(teamId, leagueId)])
            .then(([roster, players]) => {
                setPlayers(players)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [fetchRosters, gameId, leagueId, teamId])

    const toggleModal = useCallback(() => {
        setOpen(open => !open)
    }, [])

    const editRosterData = useCallback(
        (rosterId: number) => {
            const selectedRoster = roster.find(it => it.id === rosterId) || ({} as Roster)
            setSelectedRoster(selectedRoster)
            toggleModal()
        },
        [roster, toggleModal]
    )

    const submitRosterData = useCallback(
        (data: RosterFormData) => {
            if (selectedRoster) {
                setLoading(true)
                editRoster(selectedRoster.id, data).then(() => {
                    fetchRosters().finally(() => {
                        setLoading(false)
                    })
                    toggleModal()
                })
            }
        },
        [toggleModal, fetchRosters, selectedRoster]
    )

    const submitRoster = useCallback(
        async (addKeys: string[], deleteKeys: string[]) => {
            setLoading(true)

            try {
                for (let i = 0; i < deleteKeys.length; i++) {
                    const rosterId = deleteKeys[i]
                    await deleteRoster(Number(rosterId))
                }

                for (let i = 0; i < addKeys.length; i++) {
                    const playerId = addKeys[i]
                    await addPlayerToRoster({
                        teamId,
                        gameId,
                        leagueId,
                        playerId: Number(playerId),
                    })
                }
            } catch (e) {
                setLoading(false)
            }

            fetchRosters().finally(() => setLoading(false))
        },
        [fetchRosters, gameId, leagueId, teamId]
    )

    const playerStatsModalTitle = useMemo(() => {
        if (selectedRoster?.player) {
            return formatModalTitle('Player stats', `${selectedRoster.player.surname} ${selectedRoster.player.name}`)
        }

        if (selectedRoster?.playerId) {
            return formatModalTitle('Player stats', `#${selectedRoster.playerId}`)
        }

        return 'Player stats'
    }, [selectedRoster])

    return (
        <Spin spinning={loading} delay={300}>
            <RosterTransfer roster={roster} players={players} submitRoster={submitRoster} />
            <RosterTable rosters={roster} editRoster={editRosterData} />
            <Modal
                title={playerStatsModalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <RosterForm roster={selectedRoster} onSubmit={submitRosterData} />
            </Modal>
        </Spin>
    )
})
